import { AddIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import Select from "../../../shared/components/Select";
import useApi from "../../../shared/hooks/useApi";
import { OfficeId } from "../../../shared/schema/schema";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";
import { emitRefreshPlansOfCare, sectionOrderKey } from "../plan-of-care-type.utils";
import PlanOfCareDraftItemRow, { DraftItem, newDraftItem } from "./PlanOfCareDraftItemRow";

interface Props {
  offices: { id: OfficeId; name: string }[];
  onClose: () => void;
}

/**
 * Creates a plan of care type for one or more offices that do not have one yet. Replaces the
 * AngularJS `customPlanOfCareType` modal; it shares the draft row component with the editor.
 */
export default function PlanOfCareTypeCreateModal(props: Props) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [officeIds, setOfficeIds] = React.useState<OfficeId[]>([]);
  const [requirePatientSignature, setRequirePatientSignature] = React.useState(true);
  const [drafts, setDrafts] = React.useState<DraftItem[]>([]);

  const defaultItemsQuery = useQuery({
    ...queries.planOfCareType.defaultTaskItems(),
    enabled: false,
  });

  const sections = React.useMemo(
    () =>
      [...new Set(drafts.map((draft) => draft.section).filter((s) => s.trim() !== ""))].sort(
        (a, b) => a.localeCompare(b)
      ),
    [drafts]
  );

  const createMutation = useMutation({
    ...mutations.planOfCareType.createType(),
    onError: (error) =>
      toast({
        title: "Failed to create plan of care",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queries.planOfCareType.settings().queryKey });
      emitRefreshPlansOfCare();
      toast({
        title: "Custom plan of care created successfully",
        status: "success",
        position: "top-right",
      });
      props.onClose();
    },
  });

  const handleUseDefault = async () => {
    const result = await defaultItemsQuery.refetch();
    if (result.data === undefined) {
      return;
    }
    setDrafts(
      result.data.items.map((item, index) => ({
        id: `default-${index}`,
        section: item.section,
        order: item.order,
        code: item.code,
        label: item.label,
        isPersonalCare: item.isPersonalCare,
      }))
    );
  };

  const handleAddDraft = () =>
    setDrafts([...drafts, newDraftItem({ section: sections[0] ?? "", order: drafts.length + 1 })]);

  const handleChangeDraft = (id: string, patch: Partial<DraftItem>) =>
    setDrafts(drafts.map((draft) => (draft.id === id ? { ...draft, ...patch } : draft)));

  const errors = validate({ officeIds, drafts });

  const handleSubmit = () => {
    if (errors.length > 0) {
      return;
    }
    createMutation.mutate({
      officeIds,
      requirePatientSignature,
      items: drafts.map((draft) => ({
        code: draft.code.trim(),
        label: draft.label.trim(),
        section: draft.section.trim(),
        order: draft.order,
        isActive: true,
        isPersonalCare: draft.isPersonalCare,
        type: "Task" as const,
      })),
    });
  };

  return (
    <Modal isOpen={true} scrollBehavior="inside" size="6xl" onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>New plan of care configuration</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Flex align="center" gap={6} wrap="wrap">
              <Select
                allowUnselect={true}
                label="Offices"
                multiple={true}
                options={props.offices.map((office) => ({ label: office.name, value: office.id }))}
                value={officeIds.length === 0 ? null : officeIds}
                onChange={(value) => setOfficeIds(value ?? [])}
              />
              <FormControl alignItems="center" display="flex" width="auto">
                <FormLabel fontSize="sm" htmlFor="poc-create-signature" mb={0}>
                  Require patient signature
                </FormLabel>
                <Switch
                  id="poc-create-signature"
                  isChecked={requirePatientSignature}
                  onChange={(e) => setRequirePatientSignature(e.target.checked)}
                />
              </FormControl>
              <Button
                isLoading={defaultItemsQuery.isFetching}
                size="sm"
                variant="outline"
                onClick={handleUseDefault}
              >
                Use default plan of care
              </Button>
            </Flex>

            <Box borderColor="gray.200" borderRadius="md" borderWidth={1} overflowX="auto">
              <Table size="sm">
                <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th>Section</Th>
                    <Th isNumeric={true} width="80px">
                      Order
                    </Th>
                    <Th width="120px">Code</Th>
                    <Th>Task</Th>
                    <Th width="120px">Personal care</Th>
                    <Th width="60px" />
                  </Tr>
                </Thead>
                <Tbody>
                  {drafts.map((draft) => (
                    <PlanOfCareDraftItemRow
                      key={draft.id}
                      draft={draft}
                      sections={sections}
                      onChange={(patch) => handleChangeDraft(draft.id, patch)}
                      onRemove={() => setDrafts(drafts.filter((d) => d.id !== draft.id))}
                    />
                  ))}
                  {drafts.length === 0 && (
                    <Tr>
                      <Td colSpan={6}>
                        <Text color="gray.500" py={6} textAlign="center">
                          Add tasks, or start from the default plan of care
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>

            <Box>
              <Button leftIcon={<AddIcon />} size="sm" variant="outline" onClick={handleAddDraft}>
                Add task
              </Button>
            </Box>

            {errors.length > 0 && (
              <Alert borderRadius="md" status="error">
                <AlertIcon />
                <VStack align="start" spacing={0}>
                  {errors.map((error) => (
                    <Text key={error} fontSize="sm">
                      {error}
                    </Text>
                  ))}
                </VStack>
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} variant="ghost" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={errors.length > 0}
            isLoading={createMutation.isPending}
            onClick={handleSubmit}
          >
            Create
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function validate(params: { officeIds: OfficeId[]; drafts: DraftItem[] }): string[] {
  const errors: string[] = [];

  if (params.officeIds.length === 0) {
    errors.push("Please select desired office/s");
  }
  if (params.drafts.length === 0) {
    errors.push("A plan of care requires at least 1 task");
    return errors;
  }
  if (
    params.drafts.some(
      (draft) =>
        draft.label.trim() === "" || draft.code.trim() === "" || draft.section.trim() === ""
    )
  ) {
    errors.push("Every task needs a section, a code and a name");
  }

  const codes = params.drafts.map((draft) => draft.code.trim());
  if (new Set(codes).size !== codes.length) {
    errors.push("The same task code is used in more than 1 task");
  }

  const sectionOrders = params.drafts.map((draft) =>
    sectionOrderKey(draft.section.trim(), draft.order)
  );
  if (new Set(sectionOrders).size !== sectionOrders.length) {
    errors.push("The same task order is used twice in the same section");
  }

  return errors;
}
